<template>
  <nav v-if="pages > 1" class="pager" :aria-label="$t('pager.label')">
    <button
      v-if="modelValue > 1"
      class="pager__btn pager__btn--arrow"
      :aria-label="$t('pager.first')"
      @click="go(1)"
    >&laquo;</button>
    <button
      v-if="modelValue > 1"
      class="pager__btn pager__btn--arrow"
      :aria-label="$t('pager.prev')"
      @click="go(modelValue - 1)"
    >&lsaquo;</button>

    <button
      v-for="(p, i) in visiblePages"
      :key="`${i}-${p}`"
      class="pager__btn"
      :class="{ 'pager__btn--active': p === modelValue, 'pager__btn--gap': p === GAP }"
      :disabled="p === GAP"
      :aria-hidden="p === GAP ? 'true' : null"
      :aria-current="p === modelValue ? 'page' : null"
      @click="go(p)"
    >{{ p }}</button>

    <button
      v-if="modelValue < pages"
      class="pager__btn pager__btn--arrow"
      :aria-label="$t('pager.next')"
      @click="go(modelValue + 1)"
    >&rsaquo;</button>
    <button
      v-if="modelValue < pages"
      class="pager__btn pager__btn--arrow"
      :aria-label="$t('pager.last')"
      @click="go(pages)"
    >&raquo;</button>
  </nav>
</template>

<script>
// Never render more than this many page numbers — longer runs collapse into
// ellipses so the control keeps a constant width no matter the page count.
const MAX_SLOTS = 7
const GAP = '…'

export default {
  name: 'AppPagination',
  props: {
    modelValue: { type: Number, default: 1 },
    pages: { type: Number, required: true },
    // Where to scroll after a page change. Empty → the top of the window
    // (full-page lists); a CSS selector → that element (in-page sections).
    scrollTarget: { type: String, default: '' }
  },
  emits: ['update:modelValue'],
  data() {
    return { GAP }
  },
  computed: {
    // Always MAX_SLOTS entries once there are more pages than slots: the first
    // and last page stay reachable, the middle window follows the current page,
    // and the skipped runs show as a single ellipsis. e.g. 1 … 5 6 7 … 20
    visiblePages() {
      const total = this.pages
      if (total <= MAX_SLOTS) return this.range(1, total)

      const cur = Math.min(Math.max(1, this.modelValue), total)
      const edge = 4 // pages near either end are shown without a leading gap

      if (cur <= edge) return [...this.range(1, MAX_SLOTS - 2), GAP, total]
      if (cur > total - edge) return [1, GAP, ...this.range(total - (MAX_SLOTS - 3), total)]
      return [1, GAP, cur - 1, cur, cur + 1, GAP, total]
    }
  },
  methods: {
    range(from, to) {
      return Array.from({ length: to - from + 1 }, (_, i) => from + i)
    },
    go(p) {
      if (typeof p !== 'number') return // the ellipsis placeholder
      const next = Math.min(Math.max(1, p), this.pages)
      if (next === this.modelValue) return
      this.$emit('update:modelValue', next)
      const el = this.scrollTarget ? document.querySelector(this.scrollTarget) : null
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      else window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style lang="scss">
@import '../../styles/components/common/pagination.scss';
</style>
